import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionsAPI, platformAPI, fileUrl } from '../../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Printer, Send, Save, Edit3, CheckCircle, X,
  Calendar, DollarSign, Building2, FileText, User,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  .invoice-print-area, .invoice-print-area * { visibility: visible !important; }
  .invoice-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 0; margin: 0; }
  .no-print { display: none !important; }
  @page { margin: 12mm; size: A4; }
}
`;

export default function SuperAdminInvoicePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [platform, setPlatform] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [form, setForm] = useState({
    company_name: '',
    company_email: '',
    plan_name: '',
    billing_cycle: 'monthly',
    amount: 0,
    period_start: '',
    period_end: '',
    notes: '',
    terms: '',
    tax_amount: 0,
    discount_amount: 0,
  });

  useEffect(() => {
    if (!document.getElementById('invoice-print-css2')) {
      const style = document.createElement('style');
      style.id = 'invoice-print-css2';
      style.textContent = PRINT_CSS;
      document.head.appendChild(style);
    }
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [invList, platData] = await Promise.all([
        subscriptionsAPI.invoices().catch(() => []),
        platformAPI.getPublicSettings().catch(() => ({})),
      ]);
      const inv = invList.find(i => String(i.id) === String(id));
      if (inv) {
        setInvoice(inv);
        setForm({
          company_name: inv.company_name || '',
          company_email: inv.company_email || '',
          plan_name: inv.plan_name || '',
          billing_cycle: inv.billing_cycle || 'monthly',
          amount: inv.amount || 0,
          period_start: inv.period_start ? inv.period_start.split('T')[0] : '',
          period_end: inv.period_end ? inv.period_end.split('T')[0] : '',
          notes: inv.notes || '',
          terms: inv.terms || 'Payment is due within 30 days of invoice date.',
          tax_amount: inv.tax_amount || 0,
          discount_amount: inv.discount_amount || 0,
        });
      }
      setPlatform(platData);
    } catch (err) {
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save the editable fields via the invoices endpoint
      await subscriptionsAPI.updateInvoice(id, form);
      toast.success('Invoice updated!');
      setEditing(false);
      loadData();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendEmail = async () => {
    try {
      const { emailAPI } = await import('../../services/api');
      await emailAPI.send({
        to: form.company_email || invoice.company_email,
        subject: 'Invoice #' + String(invoice.id).padStart(5, '0') + ' — ' + (platform.platform_name || 'LedgerPro'),
        html: generateInvoiceHTML(form, invoice, platform),
      });
      toast.success('Invoice sent to ' + form.company_name);
    } catch (err) {
      toast.error(err.message || 'Failed to send');
    }
  };

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;
  if (!invoice) return <div style={{ padding: 40, textAlign: 'center' }}><p>Invoice not found</p><button className="sa-btn sa-btn-outline" onClick={() => navigate(-1)}>Go Back</button></div>;

  const subtotal = parseFloat(form.amount) || 0;
  const tax = parseFloat(form.tax_amount) || 0;
  const discount = parseFloat(form.discount_amount) || 0;
  const total = subtotal + tax - discount;

  return (
    <div>
      {/* Top toolbar */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button className="sa-btn sa-btn-ghost" onClick={() => navigate(-1)}>
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {editing ? (
            <>
              <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => setEditing(false)}>
                <X size={14} /> Cancel
              </button>
              <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={handleSave} disabled={saving}>
                <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => setEditing(true)}>
                <Edit3 size={14} /> Edit Invoice
              </button>
              <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={handlePrint}>
                <Printer size={14} /> Print
              </button>
              <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={handleSendEmail} style={{ color: '#8b5cf6', borderColor: '#8b5cf6' }}>
                <Send size={14} /> Send to Company
              </button>
            </>
          )}
        </div>
      </div>

      {/* INVOICE DOCUMENT */}
      <div className="invoice-print-area" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', maxWidth: 900, margin: '0 auto' }}>
        {/* HEADER */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', color: '#fff', padding: '28px 36px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {platform.logo_path ? (
              <img src={fileUrl(platform.logo_path)} alt="Logo" style={{ height: 50, width: 50, objectFit: 'contain', borderRadius: 8, background: '#fff', padding: 3 }} />
            ) : (
              <div style={{ width: 50, height: 50, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800 }}>LP</div>
            )}
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{platform.platform_name || 'LedgerPro'}</h1>
              {platform.company_address && <p style={{ margin: '2px 0 0', fontSize: 11, opacity: 0.8 }}>{platform.company_address}</p>}
              {platform.company_phone && <p style={{ margin: '2px 0 0', fontSize: 11, opacity: 0.8 }}>Tel: {platform.company_phone}</p>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 16, background: invoice.status === 'paid' ? '#16a34a' : '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
              {invoice.status === 'paid' ? 'PAID' : 'PENDING'}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 18, fontWeight: 800 }}>SUBSCRIPTION INVOICE</p>
            <p style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.8 }}>Invoice #{String(invoice.id).padStart(5, '0')}</p>
          </div>
        </div>

        {/* BILL TO & DETAILS */}
        <div style={{ padding: '24px 36px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Bill To</p>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input className="sa-input" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="Company name" />
                <input className="sa-input" value={form.company_email} onChange={e => setForm({...form, company_email: e.target.value})} placeholder="Email" />
              </div>
            ) : (
              <>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>{form.company_name}</p>
                {form.company_email && <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>{form.company_email}</p>}
              </>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <table style={{ borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Invoice Date', fmtDate(invoice.created_at)],
                  ['Billing Cycle', editing ? (
                    <select className="sa-input sa-select" value={form.billing_cycle} onChange={e => setForm({...form, billing_cycle: e.target.value})} style={{ width: 120, fontSize: 12, padding: '4px 8px' }}>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  ) : (form.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly')],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <td style={{ fontSize: 12, color: '#94a3b8', padding: '3px 12px 3px 0', textAlign: 'right' }}>{label}</td>
                    <td style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', padding: '3px 0', textAlign: 'right' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* LINE ITEMS */}
        <div style={{ padding: '20px 36px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Description</th>
                <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Plan</th>
                <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Period</th>
                <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '12px 14px', fontSize: 13 }}>
                  {editing ? <input className="sa-input" value={form.plan_name} onChange={e => setForm({...form, plan_name: e.target.value})} style={{ width: '100%' }} /> : 'Subscription — ' + form.plan_name}
                </td>
                <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: '#3b82f6' }}>{form.plan_name}</td>
                <td style={{ padding: '12px 14px', fontSize: 11, color: '#64748b' }}>
                  {editing ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input className="sa-input" type="date" value={form.period_start} onChange={e => setForm({...form, period_start: e.target.value})} style={{ fontSize: 10, padding: '3px 6px', width: 110 }} />
                      <span style={{ padding: '3px 2px' }}>to</span>
                      <input className="sa-input" type="date" value={form.period_end} onChange={e => setForm({...form, period_end: e.target.value})} style={{ fontSize: 10, padding: '3px 6px', width: 110 }} />
                    </div>
                  ) : fmtDate(form.period_start) + ' to ' + fmtDate(form.period_end)}
                </td>
                <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
                  {editing ? <input className="sa-input" type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} style={{ width: 100, textAlign: 'right' }} /> : fmt(form.amount)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: 280 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>Subtotal</span>
                <span style={{ fontSize: 12, color: '#1e293b' }}>{fmt(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>Tax</span>
                {editing ? <input className="sa-input" type="number" step="0.01" value={form.tax_amount} onChange={e => setForm({...form, tax_amount: parseFloat(e.target.value) || 0})} style={{ width: 80, textAlign: 'right', fontSize: 12, padding: '3px 6px' }} /> : <span style={{ fontSize: 12, color: '#1e293b' }}>{fmt(tax)}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>Discount</span>
                {editing ? <input className="sa-input" type="number" step="0.01" value={form.discount_amount} onChange={e => setForm({...form, discount_amount: parseFloat(e.target.value) || 0})} style={{ width: 80, textAlign: 'right', fontSize: 12, padding: '3px 6px' }} /> : <span style={{ fontSize: 12, color: '#dc2626' }}>-{fmt(discount)}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid #e2e8f0' }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Total Due</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{fmt(total)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: 14, marginTop: 8 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>Notes</p>
            {editing ? <textarea className="sa-input" rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} style={{ width: '100%' }} /> : <p style={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap' }}>{form.notes || 'No notes'}</p>}
          </div>
          <div style={{ marginTop: 10 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>Terms & Conditions</p>
            {editing ? <textarea className="sa-input" rows={2} value={form.terms} onChange={e => setForm({...form, terms: e.target.value})} style={{ width: '100%' }} /> : <p style={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap' }}>{form.terms}</p>}
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '16px 36px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>{platform.platform_name || 'LedgerPro'} — Professional Accounting Platform</p>
            {platform.company_website && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>{platform.company_website}</p>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>This is a computer-generated invoice. No signature required.</p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>Generated on {fmtDate(new Date().toISOString())}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function generateInvoiceHTML(form, invoice, platform) {
  const subtotal = parseFloat(form.amount) || 0;
  const tax = parseFloat(form.tax_amount) || 0;
  const discount = parseFloat(form.discount_amount) || 0;
  const total = subtotal + tax - discount;
  const isPaid = invoice.status === 'paid';
  return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f1f5f9;font-family:'Segoe UI',Tahoma,sans-serif;">
<div style="max-width:600px;margin:20px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.08);">
  <div style="background:linear-gradient(135deg,#0f172a,#1e3a5f);color:#fff;padding:24px 28px;display:flex;align-items:center;gap:12px;">
    <div style="width:44px;height:44px;border-radius:8px;background:rgba(255,255,255,0.15);display:inline-flex;align-items:center;justify-content:center;font-size:16px;font-weight:800;">LP</div>
    <div><h1 style="margin:0;font-size:18px;font-weight:800;">${platform.platform_name || 'LedgerPro'}</h1>
    ${platform.company_address ? '<p style="margin:2px 0 0;font-size:10px;opacity:0.8;">' + platform.company_address + '</p>' : ''}</div>
    <div style="margin-left:auto;text-align:right;">
      <span style="background:${isPaid ? '#16a34a' : '#f59e0b'};color:#fff;padding:3px 12px;border-radius:14px;font-size:10px;font-weight:700;">${isPaid ? 'PAID' : 'PENDING'}</span>
      <p style="margin:6px 0 0;font-size:11px;opacity:0.8;">Invoice #${String(invoice.id).padStart(5, '0')}</p>
    </div>
  </div>
  <div style="padding:24px 28px;">
    <p style="font-size:12px;color:#64748b;margin:0 0 4px;">Bill To</p>
    <p style="font-size:15px;font-weight:700;color:#0f172a;margin:0;">${form.company_name}</p>
    ${form.company_email ? '<p style="font-size:12px;color:#64748b;margin:4px 0 0;">' + form.company_email + '</p>' : ''}
    <table style="width:100%;border-collapse:collapse;margin:20px 0;">
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;border-bottom:1px solid #f1f5f9;">Plan</td><td style="padding:8px 12px;font-size:12px;font-weight:600;border-bottom:1px solid #f1f5f9;">${form.plan_name}</td></tr>
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;border-bottom:1px solid #f1f5f9;">Cycle</td><td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;">${form.billing_cycle}</td></tr>
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;border-bottom:1px solid #f1f5f9;">Period</td><td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;">${fmtDate(form.period_start)} to ${fmtDate(form.period_end)}</td></tr>
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;">Subtotal</td><td style="padding:8px 12px;font-size:12px;">${fmt(subtotal)}</td></tr>
      ${tax > 0 ? '<tr><td style="padding:8px 12px;font-size:11px;color:#64748b;">Tax</td><td style="padding:8px 12px;font-size:12px;">' + fmt(tax) + '</td></tr>' : ''}
      ${discount > 0 ? '<tr><td style="padding:8px 12px;font-size:11px;color:#64748b;">Discount</td><td style="padding:8px 12px;font-size:12px;color:#dc2626;">-' + fmt(discount) + '</td></tr>' : ''}
      <tr><td style="padding:8px 12px;font-size:11px;color:#64748b;font-weight:700;border-top:2px solid #e2e8f0;">Total Due</td><td style="padding:8px 12px;font-size:14px;font-weight:800;border-top:2px solid #e2e8f0;">${fmt(total)}</td></tr>
    </table>
    ${form.notes ? '<p style="font-size:11px;color:#64748b;margin:12px 0 4px;font-weight:700;">Notes:</p><p style="font-size:12px;color:#475569;margin:0;">' + form.notes + '</p>' : ''}
    ${form.terms ? '<p style="font-size:11px;color:#64748b;margin:12px 0 4px;font-weight:700;">Terms:</p><p style="font-size:12px;color:#475569;margin:0;">' + form.terms + '</p>' : ''}
  </div>
  <div style="background:#f8fafc;border-top:1px solid #e2e8f0;padding:14px 28px;text-align:center;">
    <p style="margin:0;font-size:10px;color:#94a3b8;">${platform.platform_name || 'LedgerPro'} — Professional Accounting Platform</p>
    ${platform.company_website ? '<p style="margin:2px 0 0;font-size:10px;color:#94a3b8;">' + platform.company_website + '</p>' : ''}
  </div>
</div></body></html>`;
}
